/**
 * Simulation state store for Build the Stack
 *
 * Manages the real-time simulation engine state including request processing,
 * metrics tracking, and performance monitoring using Zustand.
 */

import { create } from 'zustand'
import type {
  GameRequest,
  SimulationConfig,
  SimulationEvent,
  SimulationMetrics,
  SimulationResult,
  SimulationState,
  TileMetrics,
  VictoryCondition,
} from '../types'

/**
 * Simulation store actions interface
 */
interface SimulationActions {
  // Simulation lifecycle
  /**
   * Start the simulation with given configuration
   */
  startSimulation: (config: SimulationConfig) => void

  /**
   * Pause the currently running simulation
   */
  pauseSimulation: () => void

  /**
   * Resume a paused simulation
   */
  resumeSimulation: () => void

  /**
   * Stop and reset the simulation
   */
  stopSimulation: () => void

  // Request management
  /**
   * Add a new request to the simulation
   */
  addRequest: (request: GameRequest) => void

  /**
   * Update an existing request's status
   */
  updateRequest: (requestId: string, updates: Partial<GameRequest>) => void

  /**
   * Complete a request (move to completed list)
   */
  completeRequest: (requestId: string) => void

  /**
   * Fail a request (move to failed list)
   */
  failRequest: (requestId: string, error: string) => void

  // Metrics management
  /**
   * Update metrics for a specific tile
   */
  updateTileMetrics: (tileId: string, metrics: Partial<TileMetrics>) => void

  /**
   * Recalculate overall simulation metrics
   */
  updateSimulationMetrics: () => void

  /**
   * Reset all metrics to initial state
   */
  resetMetrics: () => void

  // Event tracking
  /**
   * Add a simulation event for tracking/debugging
   */
  addEvent: (event: Omit<SimulationEvent, 'id' | 'timestamp'>) => void

  /**
   * Clear all events
   */
  clearEvents: () => void

  // Victory conditions
  /**
   * Set victory conditions for current simulation
   */
  setVictoryConditions: (conditions: VictoryCondition[]) => void

  /**
   * Check if victory conditions have been met
   */
  checkVictoryConditions: () => boolean

  /**
   * Get final simulation result
   */
  getSimulationResult: () => SimulationResult

  // Configuration
  /**
   * Update simulation configuration
   */
  updateConfig: (config: Partial<SimulationConfig>) => void

  /**
   * Reset simulation state to defaults
   */
  resetSimulationState: () => void
}

/**
 * Combined simulation store interface
 */
interface SimulationStore extends SimulationState, SimulationActions {}

/**
 * Default simulation configuration
 */
const defaultSimulationConfig: SimulationConfig = {
  targetRequests: 1000,
  requestRate: 50, // requests per second
  maxDuration: 120000, // 2 minutes
  enableAnimations: true,
  speedMultiplier: 1.0,
  pauseOnFailure: false,
}

/**
 * Default simulation metrics
 */
const defaultSimulationMetrics: SimulationMetrics = {
  totalRequests: 0,
  successfulRequests: 0,
  failedRequests: 0,
  successRate: 0,
  averageLatency: 0,
  throughput: 0,
  peakThroughput: 0,
  uptime: 100,
  lastUpdated: Date.now(),
  tileMetrics: new Map(),
}

/**
 * Default simulation state
 */
const defaultSimulationState: SimulationState = {
  isRunning: false,
  isPaused: false,
  currentCycle: 0,
  totalCycles: 0,
  config: defaultSimulationConfig,
  activeRequests: new Map(),
  completedRequests: [],
  failedRequests: [],
  metrics: defaultSimulationMetrics,
  victoryConditions: [],
  isVictorious: false,
}

/**
 * Simulation store implementation using Zustand
 */
export const useSimulationStore = create<SimulationStore>((set, get) => ({
  ...defaultSimulationState,

  // Simulation lifecycle actions
  startSimulation: (config: SimulationConfig) => {
    const now = Date.now()

    set({
      isRunning: true,
      isPaused: false,
      startTime: now,
      endTime: undefined,
      currentCycle: 0,
      totalCycles: Math.ceil(config.targetRequests / config.requestRate),
      config,
      activeRequests: new Map(),
      completedRequests: [],
      failedRequests: [],
      metrics: {
        ...defaultSimulationMetrics,
        lastUpdated: now,
        tileMetrics: new Map(),
      },
      isVictorious: false,
      victoryTime: undefined,
    })

    get().addEvent({
      type: 'simulation-start',
      data: { config },
    })
  },

  pauseSimulation: () => {
    const state = get()

    if (!state.isRunning || state.isPaused) {
      return
    }

    set({ isPaused: true })

    get().addEvent({
      type: 'simulation-pause',
      data: { cycle: state.currentCycle },
    })
  },

  resumeSimulation: () => {
    const state = get()

    if (!state.isRunning || !state.isPaused) {
      return
    }

    set({ isPaused: false })

    get().addEvent({
      type: 'simulation-resume',
      data: { cycle: state.currentCycle },
    })
  },

  stopSimulation: () => {
    const state = get()
    const now = Date.now()

    set({
      isRunning: false,
      isPaused: false,
      endTime: now,
    })

    get().addEvent({
      type: 'simulation-stop',
      data: {
        duration: state.startTime ? now - state.startTime : 0,
        finalMetrics: state.metrics,
      },
    })
  },

  // Request management actions
  addRequest: (request: GameRequest) => {
    set(state => ({
      activeRequests: new Map(state.activeRequests).set(request.id, request),
    }))

    get().addEvent({
      type: 'request-created',
      data: { requestId: request.id },
      requestId: request.id,
    })
  },

  updateRequest: (requestId: string, updates: Partial<GameRequest>) => {
    const state = get()
    const request = state.activeRequests.get(requestId)

    if (!request) {
      return
    }

    const updatedRequest = { ...request, ...updates }

    set(state => ({
      activeRequests: new Map(state.activeRequests).set(
        requestId,
        updatedRequest
      ),
    }))

    get().addEvent({
      type: 'request-processed',
      data: { requestId, updates },
      requestId,
      tileId: updatedRequest.currentTileId,
    })
  },

  completeRequest: (requestId: string) => {
    const state = get()
    const request = state.activeRequests.get(requestId)

    if (!request) {
      return
    }

    const completedRequest = { ...request, status: 'completed' as const }
    const activeRequests = new Map(state.activeRequests)
    activeRequests.delete(requestId)

    set(state => ({
      activeRequests,
      completedRequests: [...state.completedRequests, completedRequest],
    }))

    get().addEvent({
      type: 'request-completed',
      data: { requestId, latency: request.latency },
      requestId,
    })

    // Update metrics after request completion
    get().updateSimulationMetrics()
  },

  failRequest: (requestId: string, error: string) => {
    const state = get()
    const request = state.activeRequests.get(requestId)

    if (!request) {
      return
    }

    const failedRequest: GameRequest = {
      ...request,
      status: 'failed' as const,
      error: {
        type: 'crash' as const,
        message: error,
        sourceId: request.currentTileId,
        timestamp: Date.now(),
      },
    }

    const activeRequests = new Map(state.activeRequests)
    activeRequests.delete(requestId)

    set(state => ({
      activeRequests,
      failedRequests: [...state.failedRequests, failedRequest],
    }))

    get().addEvent({
      type: 'request-failed',
      data: { requestId, error },
      requestId,
      tileId: request.currentTileId,
    })

    // Update metrics after request failure
    get().updateSimulationMetrics()
  },

  // Metrics management actions
  updateTileMetrics: (tileId: string, metrics: Partial<TileMetrics>) => {
    const state = get()
    const currentMetrics = state.metrics.tileMetrics.get(tileId) || {
      tileId,
      requestsProcessed: 0,
      requestsFailed: 0,
      averageLatency: 0,
      currentQueue: 0,
      maxQueue: 0,
      health: 1.0,
      downtime: 0,
    }

    const updatedMetrics = { ...currentMetrics, ...metrics }

    set(state => ({
      metrics: {
        ...state.metrics,
        tileMetrics: new Map(state.metrics.tileMetrics).set(
          tileId,
          updatedMetrics
        ),
        lastUpdated: Date.now(),
      },
    }))
  },

  updateSimulationMetrics: () => {
    const state = get()
    const totalCompleted = state.completedRequests.length
    const totalFailed = state.failedRequests.length
    const totalRequests = totalCompleted + totalFailed

    if (totalRequests === 0) {
      return
    }

    const successRate = (totalCompleted / totalRequests) * 100
    const averageLatency =
      totalCompleted > 0
        ? state.completedRequests.reduce((sum, req) => sum + req.latency, 0) /
          totalCompleted
        : 0

    // Calculate throughput (requests per second)
    const now = Date.now()
    const elapsed = state.startTime ? (now - state.startTime) / 1000 : 1
    const throughput = totalRequests / elapsed

    set(state => ({
      metrics: {
        ...state.metrics,
        totalRequests,
        successfulRequests: totalCompleted,
        failedRequests: totalFailed,
        successRate,
        averageLatency,
        throughput,
        peakThroughput: Math.max(state.metrics.peakThroughput, throughput),
        lastUpdated: now,
      },
    }))

    get().addEvent({
      type: 'metrics-updated',
      data: {
        totalRequests,
        successRate,
        throughput,
      },
    })

    // Check victory conditions after metrics update
    if (get().checkVictoryConditions()) {
      set({ isVictorious: true, victoryTime: now })

      get().addEvent({
        type: 'victory-achieved',
        data: { finalMetrics: get().metrics },
      })
    }
  },

  resetMetrics: () => {
    set(_state => ({
      metrics: {
        ...defaultSimulationMetrics,
        lastUpdated: Date.now(),
        tileMetrics: new Map(),
      },
    }))
  },

  // Event tracking actions
  addEvent: (event: Omit<SimulationEvent, 'id' | 'timestamp'>) => {
    const simulationEvent: SimulationEvent = {
      id: `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      ...event,
    }

    // Note: Events are not stored in state to avoid memory issues
    // In a real implementation, events would be sent to analytics/logging
    console.debug('Simulation Event:', simulationEvent)
  },

  clearEvents: () => {
    // Events are not stored in state, so this is a no-op
    // In a real implementation, this would clear the event log
  },

  // Victory conditions actions
  setVictoryConditions: (conditions: VictoryCondition[]) => {
    set({ victoryConditions: conditions })
  },

  checkVictoryConditions: (): boolean => {
    const state = get()

    if (state.victoryConditions.length === 0) {
      return false
    }

    return state.victoryConditions.every(condition => {
      const { type, target, operator } = condition
      let currentValue: number

      switch (type) {
        case 'success-rate':
          currentValue = state.metrics.successRate
          break
        case 'latency':
          currentValue = state.metrics.averageLatency
          break
        case 'throughput':
          currentValue = state.metrics.throughput
          break
        case 'uptime':
          currentValue = state.metrics.uptime
          break
        default:
          return false
      }

      switch (operator) {
        case '>=':
          return currentValue >= target
        case '<=':
          return currentValue <= target
        case '>':
          return currentValue > target
        case '<':
          return currentValue < target
        case '=':
          return currentValue === target
        default:
          return false
      }
    })
  },

  getSimulationResult: (): SimulationResult => {
    const state = get()
    const duration =
      state.endTime && state.startTime ? state.endTime - state.startTime : 0

    // Calculate grade based on success rate
    let grade: 'A' | 'B' | 'C' | 'D' | 'F'
    const successRate = state.metrics.successRate

    if (successRate >= 95) grade = 'A'
    else if (successRate >= 85) grade = 'B'
    else if (successRate >= 75) grade = 'C'
    else if (successRate >= 65) grade = 'D'
    else grade = 'F'

    // Calculate score (0-100)
    const score = Math.round(
      state.metrics.successRate * 0.6 + // 60% weight on success rate
        Math.min(state.metrics.throughput / 100, 1) * 20 + // 20% weight on throughput
        Math.max(0, (1000 - state.metrics.averageLatency) / 1000) * 20 // 20% weight on low latency
    )

    return {
      completed: !state.isRunning,
      victorious: state.isVictorious,
      finalMetrics: state.metrics,
      duration: duration / 1000, // Convert to seconds
      events: [], // Events not stored in state
      summary: {
        grade,
        score,
        strengths: [],
        improvements: [],
      },
    }
  },

  // Configuration actions
  updateConfig: (config: Partial<SimulationConfig>) => {
    set(state => ({
      config: { ...state.config, ...config },
    }))
  },

  resetSimulationState: () => {
    set(defaultSimulationState)
  },
}))
